"use client"

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { useState, useEffect, useRef } from "react"

interface GlobalPaginationProps {
  total: number
  pageSize: number
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: string) => void
  showPageSizeSelector?: boolean
  pageSizeOptions?: number[]
  isLoading?: boolean
}

export function GlobalPagination({
  total,
  pageSize,
  currentPage,
  totalPages,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 20, 30, 50, 100],
  isLoading = false
}: GlobalPaginationProps) {
  const [goToPage, setGoToPage] = useState(currentPage.toString())
  const inputRef = useRef<HTMLInputElement>(null)

  // Update input value when currentPage changes
  useEffect(() => {
    setGoToPage(currentPage.toString())
  }, [currentPage])

  // Deselect text if it gets auto-selected on mount
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      // For number inputs, we can't use setSelectionRange, but we can blur and refocus
      // This will deselect any selected text
      setTimeout(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
          inputRef.current.blur()
        }
      }, 0)
    }
  }, [])

  const handleGoToPage = () => {
    const pageNumber = parseInt(goToPage)
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage()
    }
  }

  // Always show pagination controls, even if total is 0 (will be updated when data loads)
  // Only hide if explicitly disabled or if we're in a loading state with no data
  if (total === 0 && !isLoading) {
    return null
  }

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {total > 0 ? (
          <>
            Showing {Math.min((currentPage - 1) * pageSize + 1, total)} to {Math.min(currentPage * pageSize, total)} of {total} results
          </>
        ) : isLoading ? (
          "Loading..."
        ) : (
          "No results found"
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={pageSize.toString()}
              onValueChange={onPageSizeChange}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize.toString()} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center space-x-2">
          <div className="text-sm font-medium whitespace-nowrap">
            Page {currentPage} of {totalPages}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-1">
              <Input
                ref={inputRef}
                type="number"
                min="1"
                max={totalPages}
                value={goToPage}
                onChange={(e) => setGoToPage(e.target.value)}
                onKeyPress={handleKeyPress}
                onMouseDown={(e) => {
                  // Prevent text selection when clicking on the input
                  if (document.activeElement !== e.currentTarget) {
                    e.preventDefault()
                    e.currentTarget.focus()
                  }
                }}
                tabIndex={-1}
                className="h-8 w-[80px] text-center"
                disabled={isLoading}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={handleGoToPage}
                disabled={isLoading || !goToPage}
              >
                Go
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1 || isLoading}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages || isLoading}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
